import { useCurrentUserContext } from '@/modules/auth/hooks/useCurrentUserContext';
import { useGetEmployees } from '@/modules/employees/hooks/useEmployees';
import {
  ApplicationValidationReviewer,
  ApplicationValidationStatus,
  IApplication,
  IApplicationValidation,
  IApplicationValidationReview,
} from '@/types/application';
import { ICompany } from '@/types/company';
import { UserRole } from '@/types/user';
import { PersonOutline } from '@mui/icons-material';
import { Skeleton, Tooltip, TooltipProps, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';

type ApplicationReviewerInfoProps = Omit<TooltipProps, 'title' | 'children'> & {
  application: Pick<IApplication, 'validations' | 'statuses'>;
  userRoles: UserRole[];
};

export function ApplicationReviewerInfo({
  application,
  userRoles,
}: ApplicationReviewerInfoProps) {
  const { currentUser, isLoading: isLoadingUser } =
    useCurrentUserContext() ?? {};

  const reviewDoneByKnownEmployer = (review: IApplicationValidationReview) => {
    return (
      [
        ApplicationValidationStatus.Approved,
        ApplicationValidationStatus.Rejected,
      ].includes(review.status) &&
      review.reviewer === ApplicationValidationReviewer.Employer &&
      review.reviewedBy
    );
  };

  const reviewedValidations = userRoles.includes(UserRole.Employer)
    ? application.validations?.reduce((acc, validation) => {
        const employerReviews = validation.reviews.filter(
          reviewDoneByKnownEmployer,
        );
        if (employerReviews.length > 0) {
          acc.push({
            ...validation,
            reviews: employerReviews,
          });
        }
        return acc;
      }, [] as IApplicationValidation[]) ?? []
    : [];

  const reviewerIds =
    reviewedValidations?.reduce((acc, validation) => {
      validation.reviews.forEach(review => {
        if (review.reviewedBy) {
          acc.push(review.reviewedBy);
        }
      });
      return acc;
    }, [] as string[]) ?? [];

  const { data: { data: employees = [] } = {}, isLoading: isLoadingEmployees } =
    useGetEmployees(
      (currentUser?.company as ICompany)?._id ?? '',
      {
        _id: reviewerIds,
      },
      undefined,
      {
        enabled: !!(
          !isLoadingUser &&
          (currentUser?.company as ICompany)?._id &&
          reviewerIds.length
        ),
      },
    );

  const isLoading = isLoadingUser || isLoadingEmployees;

  const reviewedValidationsWithEmployeeInfo = reviewedValidations?.map(
    validation => {
      return {
        ...validation,
        reviews: validation.reviews.map(review => {
          const reviewedBy = employees.find(
            employee => employee._id === review.reviewedBy,
          );
          return {
            ...review,
            reviewedBy,
          };
        }),
      };
    },
  );

  if (!reviewedValidationsWithEmployeeInfo?.length) {
    return null;
  }

  return isLoading ? (
    <Skeleton>
      <Typography>Reviewed by John Doe</Typography>
    </Skeleton>
  ) : (
    <div className="flex flex-col gap-2">
      {reviewedValidationsWithEmployeeInfo.map(({ reviews }) =>
        reviews.map(({ reviewedBy, reviewedAt }) => (
          <Tooltip
            arrow
            title={
              <>
                Review by{' '}
                {[reviewedBy?.firstName, reviewedBy?.lastName].join(' ')}
                {reviewedAt && isValid(new Date(reviewedAt)) && (
                  <Typography variant="caption">
                    <br /> on {format(new Date(reviewedAt), 'Pp')}
                  </Typography>
                )}
              </>
            }
            key={reviewedBy?._id}
            classes={{
              tooltip: 'bg-black text-white',
              arrow: 'text-black',
            }}
          >
            <div className="flex items-center gap-1">
              <PersonOutline className="h-4 w-4" />
              {[reviewedBy?.firstName, reviewedBy?.lastName].join(' ')}
            </div>
          </Tooltip>
        )),
      )}
    </div>
  );
}
